import React, { useEffect, useState, useCallback, useMemo } from 'react';
import * as signalR from '@microsoft/signalr';
import { fetchConfiguration, applyTheme } from '../services/configService';
import './PresentationMode.css';

interface CommitteeListItem {
  id: number;
  name: string;
  displayOrder: number;
  isActive: boolean;
  memberCount: number;
}

interface Member {
  id: number;
  personId: number;
  name: string;
  role: string;
  displayOrder: number;
}

interface CommitteeDetail {
  id: number;
  name: string;
  displayOrder: number;
  isActive: boolean;
  chair: string | null;
  coChair: string | null;
  members: Member[];
}

interface PresentationConfig {
  slideDuration: number; // in milliseconds
  displayMode: 'scrolling' | 'cloud';
  scrollSpeed: number; // pixels per second
}

interface PresentationState {
  currentCommitteeIndex: number;
  currentCommitteeId: number | null;
  isRunning: boolean;
  isStopped: boolean;
  displayMode: 'Scrolling' | 'Cloud';
  slideDurationMs: number;
  scrollSpeed: number;
  lastUpdated: string;
}

const DEFAULT_CONFIG: PresentationConfig = {
  slideDuration: 25000, // 25 seconds
  displayMode: 'scrolling',
  scrollSpeed: 30,
};

const PresentationMode: React.FC = () => {
  const [committees, setCommittees] = useState<CommitteeDetail[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isStopped, setIsStopped] = useState(false);
  const [config, setConfig] = useState<PresentationConfig>(DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [fadeState, setFadeState] = useState<'in' | 'out'>('in');
  const [remoteControlled, setRemoteControlled] = useState(false);

  // Load configuration and apply theme on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const appConfig = await fetchConfiguration();
        applyTheme(appConfig.theme);
        
        setConfig({
          slideDuration: appConfig.slideDuration.default,
          displayMode: appConfig.displayMode as 'scrolling' | 'cloud',
          scrollSpeed: appConfig.scrollSpeed.default,
        });
        
        setIsPaused(!appConfig.autoStart);
        
        console.log('Configuration loaded and applied');
      } catch (error) {
        console.error('Error loading configuration:', error);
        // Continue with defaults
      }
    };

    loadConfig();
  }, []);

  // Load active committees from API
  useEffect(() => {
    const loadCommittees = async () => {
      try {
        const response = await fetch('/api/committees?activeOnly=true');
        const data: CommitteeListItem[] = await response.json();
        
        // Load details for each committee
        const detailsPromises = data.map(async (committee: CommitteeListItem) => {
          const detailResponse = await fetch(`/api/committees/${committee.id}`);
          return detailResponse.json() as Promise<CommitteeDetail>;
        });
        
        const committeeDetails = await Promise.all(detailsPromises);
        setCommittees(committeeDetails);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading committees:', error);
        setIsLoading(false);
      }
    };

    loadCommittees();
  }, []);

  // Setup SignalR connection for remote control
  useEffect(() => {
    const connectSignalR = async () => {
      try {
        const connection = new signalR.HubConnectionBuilder()
          .withUrl('/hubs/presentation', {
            skipNegotiation: false,
            transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
          })
          .withAutomaticReconnect()
          .build();

        connection.on('StateChanged', (state: PresentationState) => {
          setRemoteControlled(true);
          setIsPaused(!state.isRunning);
          setIsStopped(state.isStopped);
          
          // Update config
          setConfig({
            slideDuration: state.slideDurationMs,
            displayMode: state.displayMode.toLowerCase() as 'scrolling' | 'cloud',
            scrollSpeed: state.scrollSpeed,
          });
        });

        connection.on('SlideChanged', (state: PresentationState) => {
          setRemoteControlled(true);
          setFadeState('out');
          setTimeout(() => {
            setCurrentIndex(state.currentCommitteeIndex);
            setFadeState('in');
          }, 500);
        });

        connection.on('ModeChanged', (state: PresentationState) => {
          setRemoteControlled(true);
          setConfig(prev => ({
            ...prev,
            displayMode: state.displayMode.toLowerCase() as 'scrolling' | 'cloud',
          }));
        });

        connection.on('TimingChanged', (state: PresentationState) => {
          setRemoteControlled(true);
          setConfig({
            slideDuration: state.slideDurationMs,
            displayMode: config.displayMode,
            scrollSpeed: state.scrollSpeed,
          });
        });

        await connection.start();
        console.log('SignalR connected');
        
        // Get initial state
        const response = await fetch('/api/presentation/state');
        const initialState: PresentationState = await response.json();
        
        if (initialState.currentCommitteeIndex !== 0 || initialState.currentCommitteeId !== null) {
          // Sync with remote state
          setRemoteControlled(true);
          setCurrentIndex(initialState.currentCommitteeIndex);
          setIsPaused(!initialState.isRunning);
          setIsStopped(initialState.isStopped);
          setConfig({
            slideDuration: initialState.slideDurationMs,
            displayMode: initialState.displayMode.toLowerCase() as 'scrolling' | 'cloud',
            scrollSpeed: initialState.scrollSpeed,
          });
        }

        return () => {
          connection.stop();
        };
      } catch (error) {
        console.log('SignalR connection failed, running in standalone mode:', error);
        return () => {};
      }
    };

    connectSignalR();
  }, [config.displayMode]);

  // Auto-advance slides (only when not remote controlled or when remote says to run)
  useEffect(() => {
    if (isPaused || isStopped || committees.length === 0) return;

    const timer = setTimeout(() => {
      setFadeState('out');
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % committees.length);
        setFadeState('in');
      }, 500); // Wait for fade-out transition
    }, config.slideDuration);

    return () => clearTimeout(timer);
  }, [currentIndex, isPaused, isStopped, committees.length, config.slideDuration]);

  const nextSlide = useCallback(() => {
    setFadeState('out');
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % committees.length);
      setFadeState('in');
    }, 500);
  }, [committees.length]);

  const previousSlide = useCallback(() => {
    setFadeState('out');
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + committees.length) % committees.length);
      setFadeState('in');
    }, 500);
  }, [committees.length]);

  // Keyboard controls (only when not remote controlled)
  useEffect(() => {
    if (remoteControlled) return; // Disable keyboard controls when remotely controlled

    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          previousSlide();
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          setIsPaused((prev) => !prev);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [nextSlide, previousSlide, remoteControlled]);

  const currentCommittee = committees[currentIndex];

  // Get regular members (exclude Chair and Co-Chair)
  const regularMembers = useMemo(() => {
    if (!currentCommittee) return [];
    return currentCommittee.members.filter(
      (m) => m.role !== 'Chair' && m.role !== 'Co-Chair'
    );
  }, [currentCommittee]);

  if (isLoading) {
    return (
      <div className="presentation-mode loading">
        <div className="loading-message">Loading presentation...</div>
      </div>
    );
  }

  if (committees.length === 0) {
    return (
      <div className="presentation-mode no-data">
        <div className="no-data-message">No active committees to display</div>
      </div>
    );
  }

  if (isStopped) {
    return (
      <div className="presentation-mode stopped">
        <div className="stopped-message">
          <div className="stopped-icon">⏹</div>
          <div className="stopped-text">Presentation will start momentarily</div>
        </div>
      </div>
    );
  }

  return (
    <div className="presentation-mode">
      <div className={`slide fade-${fadeState}`}>
        <div className="left-panel">
          <h1 className="committee-name">{currentCommittee.name}</h1>
          <div className="leadership">
            {currentCommittee.chair && (
              <div className="leadership-item">
                <span className="label">Chair:</span>
                <span className="name">{currentCommittee.chair}</span>
              </div>
            )}
            {currentCommittee.coChair && (
              <div className="leadership-item">
                <span className="label">Co-Chair:</span>
                <span className="name">{currentCommittee.coChair}</span>
              </div>
            )}
          </div>
        </div>

        <div className="right-panel">
          {config.displayMode === 'scrolling' ? (
            <ScrollingList members={regularMembers} scrollSpeed={config.scrollSpeed} />
          ) : (
            <NameCloud members={regularMembers} />
          )}
        </div>
      </div>

      {isPaused && <div className="paused-indicator">PAUSED</div>}

      <div className="progress-indicator">
        {currentIndex + 1} / {committees.length}
      </div>
    </div>
  );
};

// Scrolling list component
const ScrollingList: React.FC<{ members: Member[]; scrollSpeed: number }> = ({
  members,
  scrollSpeed,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const [isScrollable, setIsScrollable] = useState(false);

  const startOffsetRef = React.useRef(0); // px (start below container)
  const endOffsetRef = React.useRef(0); // px (end when fully out top)
  const offsetRef = React.useRef(0); // px (current translateY)

  // Measure and initialize scrolling bounds.
  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const updateMeasurements = () => {
      const containerHeight = container.clientHeight;
      // Use scrollHeight to capture full content size (independent of transforms)
      const contentHeight = content.scrollHeight;
      const shouldAnimate = members.length > 0 && contentHeight > 0;

      setIsScrollable(shouldAnimate);

      if (!shouldAnimate) {
        startOffsetRef.current = 0;
        endOffsetRef.current = 0;
        offsetRef.current = 0;
        content.style.transform = 'translateY(0px)';
        return;
      }

      // Start fully below the visible area; end when fully above the visible area.
      startOffsetRef.current = containerHeight;
      endOffsetRef.current = -contentHeight;
      offsetRef.current = startOffsetRef.current;
      content.style.transform = `translateY(${offsetRef.current}px)`;
    };

    updateMeasurements();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateMeasurements());
      resizeObserver.observe(container);
      resizeObserver.observe(content);
    } else {
      window.addEventListener('resize', updateMeasurements);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateMeasurements);
      }
    };
  }, [members]);

  // Animate the content upwards, looping only after it's fully out of view.
  useEffect(() => {
    if (!isScrollable) return;

    const content = contentRef.current;
    if (!content) return;

    let animationFrameId: number;
    let lastTimestamp = 0;

    const animate = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;

      const deltaTime = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      offsetRef.current -= scrollSpeed * deltaTime;
      if (offsetRef.current <= endOffsetRef.current) {
        offsetRef.current = startOffsetRef.current;
      }

      content.style.transform = `translateY(${offsetRef.current}px)`;
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [isScrollable, members, scrollSpeed]);

  return (
    <div className={`scrolling-list ${isScrollable ? 'scrolling' : 'no-scroll'}`} ref={containerRef}>
      <div className="scrolling-content" ref={contentRef}>
        {members.map((member) => (
          <div key={member.id} className="member-name">
            {member.name}
          </div>
        ))}
      </div>
    </div>
  );
};

// Name cloud component
// Utility function to generate deterministic size from name
const getNameSize = (name: string): 'small' | 'medium' | 'large' => {
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const sizeIndex = hash % 4;
  const sizes: ('small' | 'medium' | 'large')[] = ['small', 'medium', 'large', 'medium'];
  return sizes[sizeIndex];
};

const NameCloud: React.FC<{ members: Member[] }> = ({ members }) => {
  const cloudItems = useMemo(() => {
    return members.map((member) => ({
      ...member,
      size: getNameSize(member.name),
    }));
  }, [members]);

  return (
    <div className="name-cloud">
      {cloudItems.map((item) => (
        <span key={item.id} className={`cloud-name ${item.size}`}>
          {item.name}
        </span>
      ))}
    </div>
  );
};

export default PresentationMode;
